import type { PostHog } from 'posthog-js';

/**
 * How far PostHog's flags are from being the identified user's answer.
 *
 * A flag value cannot say which identity it was evaluated for, so this is followed from the
 * moment `identify` runs. `awaitingLoad`: the next load to finish may still be a request that
 * left before identify. `awaitingIdentifiedLoad`: a load has finished since identify; PostHog
 * sends one flags request at a time and reads the identity when it sends, so every request still
 * in flight or to come carries the identified user. `answered`: a successful load of such a
 * request has arrived, so the flags PostHog holds are the identified user's.
 */
export type IdentifiedFlagsState =
    | 'anonymous'
    | 'awaitingLoad'
    | 'awaitingIdentifiedLoad'
    | 'answered';

type FlagsClient = Pick<PostHog, 'onFeatureFlags'>;

let state: IdentifiedFlagsState = 'anonymous';
let identifiedAs: string | null = null;
let stopListening: (() => void) | undefined;
const listeners = new Set<() => void>();

function setState(next: IdentifiedFlagsState): void {
    if (state === next) return;
    state = next;
    for (const listener of [...listeners]) listener();
}

function stop(): void {
    stopListening?.();
    stopListening = undefined;
}

/**
 * Starts following the flags answer for `userId`. Call it right after `posthog.identify`, so that
 * every load it sees finished after identify ran. Following starts here and not where a flag is
 * read, because by then PostHog may already hold the identified answer and could not prove it.
 */
export function trackIdentifiedFlags(posthog: FlagsClient, userId: string): void {
    // identify runs again whenever the user object changes; the same user keeps its progress.
    if (userId === identifiedAs) return;
    stop();
    identifiedAs = userId;
    setState('awaitingLoad');
    let replaying = true;
    stopListening = posthog.onFeatureFlags((_flags, _variants, context) => {
        // onFeatureFlags replays the flags it already holds before it returns, which is not a load.
        if (replaying) return;
        if (state === 'awaitingLoad') {
            setState('awaitingIdentifiedLoad');
            return;
        }
        if (context?.errorsLoading) return;
        setState('answered');
        stop();
    });
    replaying = false;
}

/** Called with `posthog.reset`: no answer PostHog holds after it belongs to a signed-in user. */
export function resetIdentifiedFlags(): void {
    stop();
    identifiedAs = null;
    setState('anonymous');
}

export function getIdentifiedFlagsState(): IdentifiedFlagsState {
    return state;
}

export function subscribeIdentifiedFlags(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}
